import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, CheckCheck } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Erro ao buscar contador de notificações:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      fetchUnreadCount();
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      setIsOpen(false);
      const link = notification.link;
      // Links internos: path relativo OU domínio do app
      if (link.startsWith('/')) {
        navigate(link);
      } else if (link.includes('app.darkplanner.com.br')) {
        try {
          const url = new URL(link);
          navigate(url.pathname + url.search + url.hash);
        } catch {
          navigate(link);
        }
      } else {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!isOpen) {
      fetchNotifications();
      fetchUnreadCount();
    }
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Fetch unread count on mount + window focus + custom event
  useEffect(() => {
    fetchUnreadCount();
    const handleFocus = () => fetchUnreadCount();
    const handleNewNotification = () => fetchUnreadCount();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('newNotification', handleNewNotification);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('newNotification', handleNewNotification);
    };
  }, []);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (hours < 1) return 'Agora mesmo';
    if (hours < 24) return `Há ${hours} hora${hours > 1 ? 's' : ''}`;
    if (days < 7) return `Há ${days} dia${days > 1 ? 's' : ''}`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell Button — redondo 32px, alinhado aos RoundIconButton do Header */}
      <button
        onClick={toggleDropdown}
        style={{
          position: 'relative',
          width: '32px',
          height: '32px',
          padding: 0,
          background: isOpen ? 'var(--dp-bg-card)' : 'transparent',
          border: `1px solid ${isOpen ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-border-medium)'}`,
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          outline: 'none',
        }}
        onMouseEnter={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'var(--dp-bg-card)';
            e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
          }
        }}
      >
        <Bell
          size={14}
          style={{
            color: isOpen ? 'var(--dp-blue-500, #0DAEFF)' : 'var(--dp-text-secondary)',
            transition: 'color 0.2s',
          }}
        />

        {/* Badge with unread count */}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            minWidth: '16px',
            height: '16px',
            background: 'linear-gradient(135deg, #EF4444, #DC2626)',
            color: '#FFFFFF',
            fontSize: '9px',
            fontWeight: 700,
            fontFamily: 'var(--dp-font)',
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 5px',
            boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4), 0 0 0 2px var(--dp-bg-main)',
            animation: 'dpBadgePulse 2s ease-in-out infinite',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: '380px',
          maxHeight: '480px',
          background: 'var(--dp-bg-glass, rgba(28, 28, 28, 0.95))',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: '14px',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(13, 174, 255, 0.05)',
          zIndex: 9999,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'dpDropdownSlideDown 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 18px',
            borderBottom: '1px solid var(--dp-border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h3 style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 700,
                color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)',
                letterSpacing: '-0.01em',
              }}>
                Notificações
              </h3>
              {unreadCount > 0 && (
                <span style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--dp-blue-500, #0DAEFF)',
                  background: 'rgba(13, 174, 255, 0.1)',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  fontFamily: 'var(--dp-font)',
                }}>
                  {unreadCount} {unreadCount === 1 ? 'nova' : 'novas'}
                </span>
              )}
            </div>

            {notifications.length > 0 && notifications.some(n => !n.read) && (
              <button
                onClick={markAllAsRead}
                style={{
                  padding: '5px 10px',
                  background: 'transparent',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '7px',
                  color: 'var(--dp-text-tertiary)',
                  fontSize: '11px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-blue-500, #0DAEFF)';
                  e.currentTarget.style.color = 'var(--dp-blue-500, #0DAEFF)';
                  e.currentTarget.style.background = 'rgba(13, 174, 255, 0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.color = 'var(--dp-text-tertiary)';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <CheckCheck size={12} />
                Marcar todas
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            maxHeight: '400px',
          }}>
            {loading ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
              }}>
                {/* Loading skeleton */}
                {[1, 2, 3].map(i => (
                  <div key={i} style={{
                    padding: '14px 0',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: 'var(--dp-border-subtle)',
                      marginTop: '6px',
                      flexShrink: 0,
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        height: '12px',
                        width: `${70 + i * 10}%`,
                        background: 'var(--dp-border-subtle)',
                        borderRadius: '4px',
                        marginBottom: '8px',
                      }} />
                      <div style={{
                        height: '10px',
                        width: '40%',
                        background: 'var(--dp-border-subtle)',
                        borderRadius: '4px',
                        opacity: 0.5,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div style={{
                padding: '48px 20px',
                textAlign: 'center',
              }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  background: 'var(--dp-bg-elevated)',
                  border: '1px solid var(--dp-border-subtle)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px',
                }}>
                  <Bell size={24} style={{ color: 'var(--dp-text-tertiary)', opacity: 0.5 }} />
                </div>
                <p style={{
                  margin: '0 0 4px 0',
                  color: 'var(--dp-text-secondary)',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'var(--dp-font)',
                }}>
                  Tudo em dia!
                </p>
                <p style={{
                  margin: 0,
                  color: 'var(--dp-text-tertiary)',
                  fontSize: '12px',
                  fontFamily: 'var(--dp-font)',
                }}>
                  Nenhuma notificação no momento
                </p>
              </div>
            ) : (
              notifications.map((notification, idx) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '12px 18px',
                    borderBottom: idx < notifications.length - 1 ? '1px solid var(--dp-border-subtle)' : 'none',
                    background: notification.read ? 'transparent' : 'rgba(13, 174, 255, 0.04)',
                    cursor: notification.link ? 'pointer' : 'default',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = notification.read
                      ? 'var(--dp-bg-card-hover, rgba(255, 255, 255, 0.03))'
                      : 'rgba(13, 174, 255, 0.07)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.read
                      ? 'transparent'
                      : 'rgba(13, 174, 255, 0.04)';
                  }}
                >
                  {/* Unread indicator */}
                  <div style={{
                    flexShrink: 0,
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: notification.read ? 'transparent' : 'var(--dp-blue-500, #0DAEFF)',
                    marginTop: '6px',
                    boxShadow: notification.read ? 'none' : '0 0 8px rgba(13, 174, 255, 0.4)',
                    transition: 'all 0.2s',
                  }} />

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      margin: '0 0 3px 0',
                      fontSize: '13px',
                      fontWeight: notification.read ? 500 : 600,
                      color: 'var(--dp-text-primary)',
                      fontFamily: 'var(--dp-font)',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}>
                      {notification.title}
                    </p>
                    {notification.message && (
                      <p style={{
                        margin: '0 0 5px 0',
                        fontSize: '12px',
                        color: 'var(--dp-text-tertiary)',
                        fontFamily: 'var(--dp-font)',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {notification.message}
                      </p>
                    )}
                    <p style={{
                      margin: 0,
                      fontSize: '11px',
                      color: 'var(--dp-text-tertiary)',
                      fontFamily: 'var(--dp-font)',
                      opacity: 0.7,
                    }}>
                      {formatDate(notification.created_at)}
                    </p>
                  </div>

                  {/* Mark as read button (for unread notifications) */}
                  {!notification.read && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                      title="Marcar como lida"
                      style={{
                        flexShrink: 0,
                        padding: '5px',
                        background: 'transparent',
                        border: '1px solid transparent',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        opacity: 0.4,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.background = 'rgba(13, 174, 255, 0.08)';
                        e.currentTarget.style.borderColor = 'rgba(13, 174, 255, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.4';
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                      }}
                    >
                      <Check size={14} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes dpDropdownSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dpBadgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;
